import { Box, Button, Grid, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { WSPDashboardService } from '../../../../Api/WSPDashboard/WSPDashboardService';
import { WSPATRReportType } from '../../../../Model/Enum/BEE123Enums';
import { DashboardSectionType } from '../../../../Model/Enum/DashboardType';
import { DoubleColumnChart } from '../Base/DoubleColumnChart/DoubleColumnChart';
import { mapCostAndHeadcountTrainingTypeToDoubleColumnChartData } from './CostAndHeadcountTrainingTypeMapper';
import { trainingTypesList } from '../../../Constants/Lists/DropdownListOptions';
import { useForm } from 'react-hook-form';
import { WSPScorecardAtom } from '../../../../../State/WSPScorecard.Atom';
import { useRecoilValue } from 'recoil';
import { useHistory } from 'react-router-dom';
import AppMultiSelectWithSelectAll from '../../FormValidationInputs/AppMultiSelectWithSelectAll';
import { MerSETAReportService } from '../../../../Api/WSPReport/MerSETAReportService';
import moment from 'moment';
import { formControlColour, tooltipColour } from '../../BEE123.Constants';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
const hash = require("object-hash");

interface IProps {
    scorecardId?: string;
    scorecardTypeId?: string;
    chartOptions?: any;
    dashboardType?: DashboardSectionType;
    reportType: WSPATRReportType;
}

export const CostAndHeadcountTrainingChart = (props: IProps) => {
    const hashObj = {}
    const WSPScorecard = useRecoilValue(WSPScorecardAtom);
    const hashString = hash(hashObj);
    const history = useHistory();
    const [display, setDisplay] = useState('cost');
    const [counter, setCounter] = useState(0);
    const [selectedTrainingTypes, setSelectedTrainingTypes] = useState<string[]>(['LAI', 'Internal', 'External']);
    const [lastPopulatedDate, setLastPopulatedDate] = React.useState<Date | null>(null);
    const formattedDate: string = moment(lastPopulatedDate).format('DD MMMM YYYY HH:mm');

    const { control } = useForm({});
    const reportUrl = `/wsp/scorecard/${WSPScorecard.wspScorecardId}/MerSETAReport`

    useEffect(() => {
        getLastPopulatedDate();
    }, [])

    const getChartData = async () => {
        const response = await WSPDashboardService.GetTrainingChart(props.scorecardId!, props.reportType);
        return mapCostAndHeadcountTrainingTypeToDoubleColumnChartData(display == 'cost', response.data, selectedTrainingTypes);
    }

    const getLastPopulatedDate = async () => {
        try {
            const lastPopulatedDate = await MerSETAReportService.getLastPopulateDate(props.scorecardId!, props.reportType);
            if (lastPopulatedDate.data){
                setLastPopulatedDate(lastPopulatedDate.data);
            }    
        } catch (error) {
            console.log(error)
        }    
    }

    const handleToggleChange = (
        event: React.MouseEvent<HTMLElement>,
        newDisplay: string,
    ) => {
        if (newDisplay != null) {
            setCounter(counter + 1);
            setDisplay(newDisplay);
        }
    };

    const handleDropdownChange = (
        selectedTypes: string[],
    ) => {
        setSelectedTrainingTypes(selectedTypes);
        setCounter(counter + 1);
    };

    const handleRouting = () => {
        if (props.reportType === 1) {
            history.push(`${reportUrl}/ATRReport`);
        }
        else if (props.reportType === 2) {
            history.push(`${reportUrl}/WSPReport`);
        }
    };

    return (
        <>
            <Box>
                <Grid container>
                    <Grid item xs={12} margin={2}>
                        <Box style={{ float: 'left' }} width={450}>
                            <AppMultiSelectWithSelectAll
                                control={control}
                                size='medium'
                                name="trainingType"
                                label="Select a single training intervention or all"
                                options={trainingTypesList}
                                defaultValue={selectedTrainingTypes}
                                onChange={handleDropdownChange} />
                        </Box>
                        <Box style={{ float: 'right' }}>
                            <ToggleButtonGroup
                                color="primary"
                                value={display}
                                exclusive
                                onChange={handleToggleChange}
                                aria-label="Platform"
                            >
                                <ToggleButton value="cost">Cost</ToggleButton>
                                <ToggleButton value="headcount">Headcount</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <DoubleColumnChart
                            key={counter}
                            hash={hashString}
                            chartOptions={props.chartOptions}
                            fetchData={() => getChartData()}
                            isStacked={false}
                            emptyMessage={
                                <>
                                    It seems like your {WSPATRReportType[props.reportType]} report is empty.
                                    <br />
                                    Please take a moment to ensure that your {WSPATRReportType[props.reportType]} report is populated.
                                    <p />
                                    <Button variant='contained' onClick={handleRouting}>
                                        Go to {WSPATRReportType[props.reportType]} Report
                                    </Button>
                                </>
                            }
                        />
                    </Grid>
                </Grid>
                {lastPopulatedDate && (
                    <Grid item xs={12} textAlign="left" marginTop={2} marginLeft={1}>
                        <Typography>
                            <span style={{ fontStyle: 'italic' }}>as at </span>
                            <span style={{ fontWeight: 'bold' }}>{formattedDate}</span>
                            <Tooltip
                                title={
                                    <React.Fragment>
                                        This chart is based on the {WSPATRReportType[props.reportType]} Report which was last populated at <strong>{formattedDate}</strong>.
                                    </React.Fragment>
                                }
                                arrow
                                placement="right"
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            bgcolor: tooltipColour,
                                            maxWidth: '250px',
                                            '& .MuiTooltip-arrow': {
                                                color: tooltipColour
                                            },
                                        },
                                    },
                                }}>
                                <IconButton
                                    size="small"
                                    edge="end"
                                    color="inherit"
                                    aria-label="info"
                                    style={{ marginTop: '-2px' }}
                                >
                                    <InfoOutlinedIcon style={{ color: formControlColour }} />
                                </IconButton >
                            </Tooltip>
                        </Typography>
                    </Grid>
                    )}
            </Box>
        </>
    )
};


